import { ReactElement, useEffect, useState } from 'react'
import { useIntl, defineMessages, FormattedMessage } from 'react-intl'

import FormLayout from 'components/v2/FormLayout'
import Heading from 'components/v2/Heading'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { useLogin } from 'features/Login/hooks'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { canWrite } from 'features/Users/utils'
import { NewOperationalCosts } from 'features/OperationalCosts/components/NewOperationalCosts'
import { useMutation } from '@apollo/client'
import { CHANGE_OPERATIONAL_COST_SETTINGS, CREATE_OPERATIONAL_COST } from 'features/OperationalCosts/mutations'
import { INewOperationalCostsFormValues } from 'features/OperationalCosts/types'
import { FormikHelpers } from 'formik'
import { OperationalCostVehicleType } from 'gql/generated/graphql'
import { factorMultipliedBy100, integerToCents, kilometersToMeters } from 'utils/number'

const messages = defineMessages({
  helmet: {
    id: 'operationalCosts.new.helmet',
    defaultMessage: 'New Operational Cost | Nexbus Admin'
  }
})

const OperationalCostsView = (): ReactElement => {
  const intl = useIntl()
  const login = useLogin()
  const [error, setError] = useState<Error | null>(null)
  const [createOperationalCost] = useMutation(CREATE_OPERATIONAL_COST)
  const [changeOperationalCostSettings] = useMutation(CHANGE_OPERATIONAL_COST_SETTINGS)
  const canUpdateOperationalCost = canWrite(FEATURES.OPERATIONAL_COST, login.accessToken.policies)

  useEffect(() => {
    document.title = intl.formatMessage(messages.helmet)
  }, [intl])

  if (!canUpdateOperationalCost) {
    return <ForbiddenView feature={FEATURES.OPERATIONAL_COST} />
  }

  const handleSubmit = async (
    values: INewOperationalCostsFormValues,
    { setSubmitting, resetForm }: FormikHelpers<INewOperationalCostsFormValues>
  ): Promise<void> => {
    try {
      const createOperationalCostResponse = await createOperationalCost({
        variables: {
          input: {
            stateCode: values.stateCode,
            vehicleCapacity: values.vehicleCapacity as number,
            vehicleType: values.vehicleType as OperationalCostVehicleType,
            businessClientID: values.businessClient?.id || null,
            businessSiteID: values.businessSite?.id || null
          }
        }
      })
      await changeOperationalCostSettings({
        variables: {
          input: {
            id: createOperationalCostResponse.data?.createOperationalCost.id,
            costs: {
              durationCost: integerToCents(values.durationCost),
              costPerKm: integerToCents(values.costPerKm),
              costPerDriver: integerToCents(values.costPerDriver),
              fixedCost: integerToCents(values.fixedCost)
            },
            factors: {
              airConditioning: factorMultipliedBy100(values.airConditioning as number),
              baseDistance: kilometersToMeters(values.baseDistance as number),
              perKm: factorMultipliedBy100(values.perKm as number),
              redZone: factorMultipliedBy100(values.redZone as number),
              duration: values.durationSettings.map((durationSetting) => ({
                start: durationSetting.range.start as number,
                end: durationSetting.range.end as number,
                value: factorMultipliedBy100(durationSetting.factor as number)
              }))
            }
          }
        }
      })
      resetForm()
    } catch (error) {
      setError(error as Error)
    }
    setSubmitting(false)
  }

  return (
    <FormLayout.Container>
      <FormLayout.Content>
        <Heading.H3>
          <FormattedMessage id="partnerCost.operationalCosts.title" defaultMessage="Operational Costs" />
        </Heading.H3>
        <NewOperationalCosts onSubmit={handleSubmit} error={error} />
      </FormLayout.Content>
    </FormLayout.Container>
  )
}

export default withLastUrl(OperationalCostsView)
